import { useRequest } from 'ahooks'
import { Modal, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { isNaN, isNumber, partition } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { departmentsAtom } from '@/modules/department'
import { DepartmentUser } from '@/modules/project/project-create/project-form.component'
import { userInfoAtom, usersAtom } from '@/modules/user'
import { ResourceType, resourceTypeTabMap } from '@/schemas'

import { DepartmentTreeTransfer } from '../department-tree-transfer.component'
import { onDepartmentUserCheck } from '../department-tree-with-users.component'

/**
 * // 通用的各个类型资源 白名单配置
 * @param resourceType 资源类型
 * @param visible 是否展示
 * @param onVisibleChange 变更是否展示
 * @returns ReactNode Dialog
 */
export const ResourceWhitelistPermissionDialog: React.FC<{
  resourceType: ResourceType
  visible: boolean
  onVisibleChange: any
}> = ({ resourceType, visible, onVisibleChange }) => {
  const { t } = useTranslation()
  const [users] = useAtom(usersAtom)
  const [departments] = useAtom(departmentsAtom)
  const [userInfo] = useAtom(userInfoAtom)
  const [tabKey, setTabKey] = React.useState('WHITELIST')

  const { data: whitelist, refresh } = useRequest<string[]>(
    {
      url: '/api/permissions/getWhiteListUsersByTypeAndKey',
      params: {
        resourceType,
        roleKey: tabKey,
      },
    },
    {
      refreshDeps: [resourceType, tabKey],
    }
  )

  const { run, loading } = useRequest(
    (emails: string[]) => ({
      url: '/api/permissions/addWhiteListUsersByTypeKey',
      method: 'post',
      data: {
        resourceType,
        emails,
        roleKey: tabKey,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        refresh()
      },
    }
  )

  React.useEffect(() => {
    const defaultCheckedKeys =
      (whitelist || []).length > 0
        ? (whitelist || [])
            .map((email) => {
              // userId
              const user = users.find((user) => user.email === email)
              return user ? `${user.departmentIds[0]}-${user.email}` : null
            })
            .filter(Boolean)
            .map((id) => id!)
        : []
    onDepartmentUserCheck(defaultCheckedKeys, departments, users, setDepartmentUsers)
  }, [whitelist])

  const [departmentUsers, setDepartmentUsers] = React.useState<DepartmentUser[]>([])

  const hideModal = () => {
    onVisibleChange(false)
  }
  const handleCancel = () => {
    hideModal()
  }
  const handleOk = () => {
    const departmentUserIds: string[] = departmentUsers.map((item) => item.id)
    const [_, userIds] = partition(departmentUserIds, (id) => isNumber(Number(id)) && !isNaN(Number(id)))
    const emails = userIds.map((id) => id.split('-')[1])
    run(emails)
  }

  return (
    <Modal
      width={700}
      title={t('resource.whitelist')}
      visible={visible}
      destroyOnClose
      onOk={handleOk}
      okButtonProps={{ loading, disabled: loading }}
      onCancel={handleCancel}
    >
      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
        <Tabs onChange={(key) => setTabKey(key)} activeKey={tabKey}>
          {(resourceTypeTabMap[resourceType] || resourceTypeTabMap['default']).map((tab: string) => (
            // TODO 翻译tab标题
            <Tabs.TabPane tab={t(tab)} key={tab}>
              <DepartmentTreeTransfer
                users={users}
                departments={departments}
                userInfo={userInfo}
                defaultPermissions={whitelist}
                departmentUsers={departmentUsers}
                setDepartmentUsers={setDepartmentUsers}
              />
            </Tabs.TabPane>
          ))}
        </Tabs>
      </div>
    </Modal>
  )
}
